import OfferDetailTableHeaderData from '@components/pages/Products/components/RouteProduct/components/ShoulderItemHeaderData';
import ShoulderItemTable from '@components/pages/Products/components/RouteProduct/components/ShoulderItemTable';
import { WithRouteProductContextData } from '@components/pages/Products/components/RouteProduct/types';
import WithRouteProductContext from '@components/pages/Products/components/RouteProduct/WithRouteProductContext';
import { getLocalizationById } from '@context/useMyRouting/helpers';
import { TaxNameProps } from '@helpers/getTaxNames';
import Typography from '@mui/material/Typography';
import { generateLocalizedName } from '@pages/AdditionalServices/tabs/DeliveryForm/components/RoutesListComponent/helpers';
import useDelivery from '@pages/AdditionalServices/tabs/DeliveryForm/useDelivery';
import {
  calculateCargoForContainer,
  calculateCargoForSingleCustom,
  calculateCargoForTruck,
} from '@pages/Basket/components/ServicesList/components/OrderList/components/OrderListItem/helpers';
import { getFormattedShoulderValidDate } from '@pages/OfferDetail/helpers';
import ShoulderItemDetailsDTO, {
  RowProps,
} from '@services/collectors/ProductService/ShoulderItemDetailsDTO';
import { Order } from '@services/requests/orderService/interface';
import {
  ContainerParameters,
  CustomCargoParameters,
  RouteInGroup,
  RouteStep,
  TruckParameters,
} from '@services/requests/orderService/types';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import ShoulderItemHeader from './ShoulderItemHeader';
import ShoulderItemStyledContainer from './StyledComponents';

/**
 * Пропсы компонента
 */
type ShoulderItemProps = WithRouteProductContextData<{
  step: RouteStep;
  taxNames: TaxNameProps[];
  order: Order;
  isUseActualCourses: boolean;
  routeIndex: number;
  routes: RouteInGroup[];
  cargoType: 'container' | 'custom' | 'truck';
  containerParameters: ContainerParameters[];
  customCargoParameters: CustomCargoParameters[];
  truckParameters: TruckParameters[];
}>;

/**
 * Компонент вывода информации о плече и его терминалах
 * @param props
 */
const ShoulderItem = (props: ShoulderItemProps) => {
  const langCode = getLocalizationById(props.order.language_id).countryCode;
  const { t } = useTranslation('OfferDetail', { lng: langCode });

  const { useBaseDate } = useDelivery();
  const [baseData] = useBaseDate().useState();

  const {
    step,
    currencyRoute,
    realTimeBaseData,
    productData,
    taxNames,
    order,
    isUseActualCourses,
    routeIndex,
    cargoType,
    containerParameters,
    customCargoParameters,
    truckParameters,
	routes
  } = props;
  const {
    shoulderOffer,
    shoulderOfferCalculatedPrice,
    shoulderOfferPriceCondition,
    shoulderAllowances,
    shoulder,
    shoulderOfferAssignments,
  } = step;
  const assignments = shoulderOfferAssignments ?? [];

  const currencies = isUseActualCourses
    ? realTimeBaseData.currencies
    : productData.baseData.currencies;
  const column = OfferDetailTableHeaderData(currencyRoute, order);

  const { loadingTransportConditions, unloadingTransportConditions, containers, trucks, units } =
    baseData ?? productData.baseData;

  const startLoadingCondition = step.shoulderOffer.loading_condition_id;
  const endUnloadingCondition = step.shoulderOffer.unloading_condition_id;

  const loadingCondition = loadingTransportConditions.find((c) => c.id === startLoadingCondition);
  const unloadingCondition = unloadingTransportConditions.find(
    (c) => c.id === endUnloadingCondition
  );

  const stepShoulderType = realTimeBaseData.shoulderTypes.find(
    (s) => s.id === step.shoulder.shoulder_type
  );
  const allowancesData = (shoulderAllowances ?? []).filter((a) => a.allowanceOffer.id !== '1');

  const allowancesDataFiltered: RowProps[] = [];

  allowancesData.forEach((item) => {
    const allowance = realTimeBaseData?.allowancesData.find(
      (ac) => ac.id === item.allowanceOffer.allowance_id
    );
    const title = allowance
      ? generateLocalizedName(
          allowance.default_name,
          allowance.localized_names_data,
          order.language_id
        )
      : '';
    const baseCurrency = currencies.find(
      (rc) => rc.id === item.allowanceOfferCondition.currency_id
    );

    if (baseCurrency) {
      allowancesDataFiltered.push({
        price: item.calculatedPrice,
        name: title,
        baseCurrency: baseCurrency || 0,
        targetCurrency: currencyRoute,
        taxName:
          taxNames.find((i) => +i.taxId === item.allowanceOfferCondition.tax_id)?.localizedName ||
          '',
      } as RowProps);
    }
  });

  // Основной перевозчик
  const carrier = useMemo(
    () => realTimeBaseData.carriers.find((c) => c.id === step.shoulder.carrier_id),
    [realTimeBaseData.carriers, step]
  );

  const carrierName =
    (carrier &&
      generateLocalizedName(
        carrier.default_name,
        carrier.localized_names_data,
        order.language_id
      )) ||
    '';
  const { routePointsItems, terminals } = productData;

  const { from: startLocation, to: endLocation } = routePointsItems;

  const isStartFromTerminal = !!step.startTerminal;

  let startPoint = { ...startLocation };
  if (isStartFromTerminal) {
    const terminal = terminals.find((t) => t.id === step.startTerminal.id);
    if (terminal) {
      startPoint = { ...terminal };
    }
  }

  const isEndFromTerminal = !!step.endTerminal;

  let endPoint = { ...endLocation };
  if (isEndFromTerminal) {
    const terminal = terminals.find((t) => t.id === step.endTerminal.id);
    if (terminal) {
      endPoint = { ...terminal };
    }
  }

  const shoulderSteps = shoulder.shoulderSteps.sort((a, b) => (a.position < b.position ? -1 : 1));

  let transportTypes = [`${stepShoulderType?.transport_type}`];
  if (!stepShoulderType?.transport_type) {
    transportTypes = shoulderSteps.map((step) => step.transportType.id);
  }

  const locations = [startPoint];

  if (!stepShoulderType?.transport_type) {
    shoulderSteps.map((step) => {
      if (!step.endTerminal) {
        return;
      }

      const terminal = terminals.find((t) => t.id === step.endTerminal?.id);
      if (!terminal) {
        return;
      }

      locations.push(terminal);
    });
  }

  locations.push(endPoint);

  const totalPrice = (shoulderAllowances ?? []).reduce(
    (sum, allowance) =>
      sum +
      allowance.calculatedPrice.priceInTargetCurrency +
      allowance.calculatedPrice.taxInTargetCurrency,
    shoulderOfferCalculatedPrice.priceInTargetCurrency +
      shoulderOfferCalculatedPrice.taxInTargetCurrency
  );

  const stepCargoId = routes[routeIndex].cargoID;
  let baseCargo = '';
  switch (cargoType) {
    case 'container':
      const cargoContainer = containerParameters.find((c) => c.identifier === stepCargoId);
      baseCargo = cargoContainer
        ? calculateCargoForContainer(containers, cargoContainer, order.language_id)
        : '';
      break;
    case 'truck':
      const cargoTruck = truckParameters.find((t) => t.identifier === stepCargoId);
      baseCargo = cargoTruck ? calculateCargoForTruck(trucks, cargoTruck, order.language_id) : '';
      break;
    case 'custom':
      const cargoCustom = customCargoParameters.find((c) => c.identifier === stepCargoId);
      baseCargo = cargoCustom
        ? calculateCargoForSingleCustom(units, cargoCustom, langCode, order.language_id)
        : '';
      break;
  }

  const baseRateRow: RowProps = {
    baseCurrency: currencies.find((c) => c.id === shoulderOfferPriceCondition.currency_id),
    price: shoulderOfferCalculatedPrice,
    targetCurrency: currencyRoute,
    taxName:
      taxNames.find((i) => +i.taxId === shoulderOfferPriceCondition.tax_id)?.localizedName || '',
    name: `${t('OfferDetail:Rate')} (${loadingCondition.code} - ${unloadingCondition.code})${
      baseCargo.length > 0 ? `\n${baseCargo}` : ''
    }`,
  };

  const mixedShoulderOffers: RowProps[] = assignments.map((assignment) => {
    const startAssignmentLoadingCondition = assignment.offer.loading_condition_id;
    const endAssignmentUnloadingCondition = assignment.offer.unloading_condition_id;

    const loadingAssignmentCondition = loadingTransportConditions.find(
      (c) => c.id === startAssignmentLoadingCondition
    );
    const unloadingAssignmentCondition = unloadingTransportConditions.find(
      (c) => c.id === endAssignmentUnloadingCondition
    );

    const cargo =
      assignment.transportKind === 'container'
        ? calculateCargoForContainer(
            containers,
            {
              containerId: assignment.transportTypeId.toString(),
              containerQuantity: assignment.quantity,
              eachContainerWeight: Math.ceil(assignment.weight / assignment.quantity),
              palletsLoaded: [],
              withPallets: false,
              identifier: '',
            },
            order.language_id
          )
        : calculateCargoForTruck(
            trucks,
            {
              truckId: assignment.transportTypeId.toString(),
              truckQuantity: assignment.quantity,
              eachTruckWeight: Math.ceil(assignment.weight / assignment.quantity),
              palletsLoaded: [],
              withPallets: false,
              identifier: '',
            },
            order.language_id
          );
    return {
      baseCurrency: currencies.find((c) => c.id === assignment.offerCondition.currency_id),
      price: assignment.calculatedPrice,
      targetCurrency: currencyRoute,
      taxName:
        taxNames.find((i) => +i.taxId === assignment.offerCondition.tax_id)?.localizedName || '',
      name: `${t('OfferDetail:Rate')} (${loadingAssignmentCondition.code} - ${
        unloadingAssignmentCondition.code
      })\n${cargo}`,
    };
  });

  const baseRows = [...mixedShoulderOffers];

  if (mixedShoulderOffers.length === 0) {
    baseRows.unshift(baseRateRow);
  }

  const rowsData = ShoulderItemDetailsDTO([...baseRows, ...allowancesDataFiltered]);

  const shoulderAssignmentAddInfo = assignments.map((assignment, index) => (
    <Typography
      key={'assignment-' + index}
      variant={'subtitle2'}
      component={'div'}
      className="add-info"
    >
      {t('OfferDetail:Code')}: {assignment.offer.id}/{assignment.offer.shoulder_id}{' '}
      {t('OfferDetail:To')} {getFormattedShoulderValidDate(assignment.offer, langCode)}
    </Typography>
  ));

  const deliveryTime = Math.max(
    shoulderOffer.delivery_time,
    ...assignments.map((a) => a.offer.delivery_time)
  );

  if (totalPrice === 0) {
    return <></>;
  }

  return (
    <>
      <div>
        <ShoulderItemStyledContainer className="kendo-pdf--prevent-split">
          <ShoulderItemTable
            header={
              <ShoulderItemHeader
                locations={locations}
                fullPrice={totalPrice}
                currency={currencyRoute}
                deliveryTime={deliveryTime}
                transportType={transportTypes}
                order={order}
              />
            }
            rows={rowsData}
            columns={column}
          />
          <div className={'offer-footer-info'}>
            <Typography variant={'subtitle1'} component={'div'}>
              {!!carrier && `${t('OfferDetail:Carrier')}: ${carrierName}`}
            </Typography>
            <div className="shoulder-info-wrapper">
              {assignments.length === 0 && (
                <Typography variant={'subtitle2'} component={'div'} className="add-info">
                  {t('OfferDetail:Code')}: {step.shoulderOffer.id}/{step.shoulder.id}{' '}
                  {t('OfferDetail:To')}{' '}
                  {getFormattedShoulderValidDate(step.shoulderOffer, langCode)}
                </Typography>
              )}
              {shoulderAssignmentAddInfo}
            </div>
          </div>
        </ShoulderItemStyledContainer>
      </div>
    </>
  );
};

export default WithRouteProductContext(ShoulderItem);
